import type { TowerShipment } from "../../../types/controlTower";
import { statusText, statusTone } from "../model/shipmentStatus";
import type { RouteStop } from "../model/driverRoute";

// Marcadores del mapa del chofer (Leaflet): colores por estado, ventanas con
// los datos del envío y las paradas numeradas del recorrido.

export const STATUS_COLORS: Record<string, string> = {
  inTransit: "#e5ad3f",
  delivered: "#48bd8d",
  pending: "#5a9cff",
  incident: "#df6666",
  cancelled: "#a97575",
  other: "#a7a7ad",
};
export const LAST_DELIVERY_COLOR = "#43bdf2";
export const ROUTE_LINE_COLOR = "#43bdf2";
// En camino particular: violeta, para no confundirlo con Flex (amarillo).
export const NON_FLEX_IN_TRANSIT_COLOR = "#9384d4";

function markerColor(shipment: TowerShipment) {
  if (shipment.status === "inTransit" && shipment.shippingUniverse === "NON_FLEX") return NON_FLEX_IN_TRANSIT_COLOR;
  return STATUS_COLORS[statusTone(shipment)] || STATUS_COLORS.other;
}

// Ventana del marcador, armada con textContent: los datos importados nunca se
// interpretan como HTML.
function popupContent(shipment: TowerShipment, isLastDelivery: boolean): HTMLElement {
  const content = document.createElement("div");
  if (isLastDelivery) {
    const label = document.createElement("strong");
    label.textContent = "Última entrega registrada";
    content.appendChild(label);
  }
  const tracking = document.createElement("strong");
  tracking.textContent = shipment.tracking;
  content.appendChild(tracking);
  [statusText(shipment), shipment.client, shipment.address, shipment.locality].filter(Boolean).forEach((value) => {
    const line = document.createElement("div");
    line.textContent = value;
    content.appendChild(line);
  });
  return content;
}

// Marcador de un envío. Los de días anteriores (sin movimiento hoy) llevan el
// color pleno de su estado con el ícono ↺ y borde punteado; la última entrega,
// celeste y más grande.
export function addShipmentMarker(
  L: any,
  layer: any,
  shipment: TowerShipment & { latitude: number; longitude: number },
  { isLastDelivery, isSelected }: { isLastDelivery: boolean; isSelected: boolean },
) {
  const color = isLastDelivery ? LAST_DELIVERY_COLOR : markerColor(shipment);
  const isPrevious = shipment.carryover && !isLastDelivery;
  const title = `${isLastDelivery ? "Última entrega · " : isPrevious ? "De días anteriores · " : ""}${shipment.tracking} · ${statusText(shipment)}`;
  const position = [shipment.latitude, shipment.longitude];
  const marker = isPrevious
    ? L.marker(position, {
        icon: L.divIcon({
          className: `tower-prev-marker${isSelected ? " selected" : ""}`,
          html: `<span style="background:${color}">↺</span>`,
          iconSize: [22, 22],
          iconAnchor: [11, 11],
        }),
        title,
      })
    : L.circleMarker(position, {
        radius: isLastDelivery ? 11 : isSelected ? 10 : 8,
        color: isLastDelivery || isSelected ? "#ffffff" : "#17181b",
        weight: isLastDelivery || isSelected ? 3 : 2,
        fillColor: color,
        fillOpacity: 0.96,
        title,
      });
  return marker.bindPopup(popupContent(shipment, isLastDelivery)).addTo(layer);
}

// Recorrido: la línea en el orden de las visitas y una parada numerada por
// visita. Devuelve las posiciones para encuadrar el mapa.
export function addRouteStops(L: any, layer: any, stops: RouteStop[]): [number, number][] {
  const positions = stops.map((stop) => [stop.shipment.latitude, stop.shipment.longitude] as [number, number]);
  if (positions.length > 1) L.polyline(positions, { color: ROUTE_LINE_COLOR, weight: 3, opacity: 0.8 }).addTo(layer);
  stops.forEach((stop, index) => {
    const color = STATUS_COLORS[statusTone(stop.shipment)] || STATUS_COLORS.other;
    const content = popupContent(stop.shipment, false);
    const label = document.createElement("strong");
    label.textContent = `Parada ${stop.order} · ${stop.time}`;
    content.prepend(label);
    L.marker(positions[index], {
      icon: L.divIcon({
        className: "tower-route-stop",
        html: `<span style="background:${color}">${stop.order}</span>`,
        iconSize: [26, 26],
        iconAnchor: [13, 13],
      }),
      title: `Parada ${stop.order} · ${stop.time} · ${stop.shipment.tracking}`,
      zIndexOffset: stop.order,
    })
      .bindPopup(content)
      .addTo(layer);
  });
  return positions;
}
